import React, { useMemo } from 'react';
import { FaCheck, FaFlask } from 'react-icons/fa';
import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { useBetaFeatures } from '../../../hooks/useBetaFeature';
import { useFeatureList, CommunityFeatures } from '../../../utils/FeatureUtils';

interface FeaturesFormProps {
  features: CommunityFeatures;
  onFeaturesChange: (features: CommunityFeatures) => void;
  className?: string;
  isBusiness?: boolean;
}

interface FeatureCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  beta?: boolean;
  description: string;
  isSelected: boolean;
  onToggle: () => void;
}

function FeatureCard({ icon: Icon, title, beta, description, isSelected, onToggle }: FeatureCardProps) {
  return (
    <div
      onClick={onToggle}
      className={classNames(
        'group relative cursor-pointer rounded-xl border-2 p-4 pt-2 transition-all duration-300 hover:shadow-md',
        {
          'border-primary-900 bg-opacity-10 shadow-sm': isSelected,
          'border-secondary-200 bg-secondary-200 bg-opacity-30 hover:border-primary-900 hover:bg-opacity-40':
            !isSelected,
        },
      )}>
      <div
        className={classNames(
          'absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all duration-200',
          {
            'border-primary-900 bg-primary-900': isSelected,
            'border-secondary-200 group-hover:border-primary-900': !isSelected,
          },
        )}>
        {isSelected && <FaCheck className="h-2.5 w-2.5 text-secondary-50" />}
      </div>

      <div
        className={classNames(
          'mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-200',
          {
            'bg-primary-900 text-secondary-200': isSelected,
            'bg-secondary-200 text-primary-900':
              !isSelected,
          },
        )}>
        <Icon className="h-5 w-5" />
      </div>

      <div className="space-y-1">
        <div className='flex items-center'>
          <h3 className="text-base font-semibold text-primary-900">{title}</h3>
          {beta && (
          <span className="ml-2 inline-flex items-center px-2 py-1 rounded text-[10px] font-medium bg-secondary text-primary-900">
            <FaFlask className="h-3 w-3 mr-1" />
            BETA
          </span>
          )}
        </div>
        <p className="text-xs leading-relaxed text-primary-900 opacity-75">{description}</p>
      </div>
    </div>
  );
}

export default function FeaturesForm({
  features,
  onFeaturesChange,
  className,
  isBusiness = false,
}: FeaturesFormProps): JSX.Element {
  const communityFeatureList = useFeatureList();
  const featureList = communityFeatureList;
  //const businessFeatureList = useBusinessFeatureList();
  //const featureList = isBusiness ? businessFeatureList : communityFeatureList;

  const betaFeatureKeys = useMemo(() => {
    return featureList
      .filter(f => f.betaFeatureKey)
      .map(f => f.betaFeatureKey as string);
  }, [featureList]);

  const betaAccess = useBetaFeatures(betaFeatureKeys);

  const availableFeatures = useMemo(() => {
    return featureList.filter(config => {
      if (config.businessOnly && !isBusiness) return false;
      if (!config.betaFeatureKey) return true;
      return betaAccess[config.betaFeatureKey] === true;
    });
  }, [featureList, betaAccess, isBusiness]);

  return (
    <div className={twMerge('flex flex-1 flex-col', className)}>
      <div
        className="grid gap-3 lg:gap-4"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}>
        {availableFeatures.map((config) => (
          <FeatureCard
            key={config.key}
            icon={config.icon}
            title={config.title}
            beta={config.betaFeatureKey !== undefined}
            description={config.description}
            isSelected={Boolean(features[config.key])}
            onToggle={() => {
              onFeaturesChange({ ...features, [config.key]: !features[config.key] });
            }}
          />
        ))}
      </div>
    </div>
  );
}
